import { NavLink } from 'react-router-dom';
import { Badge, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '@shared/config/routes';
import { pickCounterpart, type ConversationView } from '@entities/chat';
import { useSessionStore } from '@entities/session';
import { Avatar } from '@shared/ui';

const { Text } = Typography;

export function ConversationRow({ conversation }: { conversation: ConversationView }) {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const ownUserId = useSessionStore((s) => s.user?.id);
  const cp = pickCounterpart(conversation, ownUserId);

  const isOwnLast = Boolean(
    ownUserId && conversation.lastMessageSenderId && conversation.lastMessageSenderId === ownUserId,
  );
  const attachmentPrefix = conversation.lastMessageHasAttachments ? '📎 ' : '';
  const previewText = conversation.lastMessagePreview
    ? `${isOwnLast ? `${t('chats.youPrefix')} ` : ''}${attachmentPrefix}${conversation.lastMessagePreview}`
    : null;

  return (
    <NavLink
      to={ROUTES.chatDetail(conversation.id)}
      style={({ isActive }) => ({
        display: 'flex',
        gap: 10,
        padding: '12px 14px',
        borderBottom: `1px solid ${token.colorSplit}`,
        background: isActive ? token.colorFillTertiary : 'transparent',
        textDecoration: 'none',
        color: 'inherit',
        transition: 'background 150ms ease',
      })}
    >
      <div
        style={{
          position: 'relative',
          flexShrink: 0,
          alignSelf: 'flex-start',
          width: 36,
          height: 36,
          lineHeight: 0,
        }}
      >
        <Avatar seed={cp.id} initials={cp.initials} src={cp.avatarUrl ?? undefined} size="md" />
        {conversation.counterpartOnline ? (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              bottom: -1,
              right: -1,
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: '#10b981',
              border: `2px solid ${token.colorBgContainer}`,
            }}
          />
        ) : null}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <Text strong style={{ fontSize: 13 }} ellipsis>
            {cp.name}
          </Text>
          {conversation.unreadCount > 0 ? <Badge count={conversation.unreadCount} size="small" /> : null}
        </div>
        {previewText ? (
          <Text type="secondary" style={{ fontSize: 12, display: 'block' }} ellipsis>
            {previewText}
          </Text>
        ) : null}
        {conversation.lastMessageAt ? (
          <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {new Date(conversation.lastMessageAt).toLocaleString([], {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Text>
        ) : null}
      </div>
    </NavLink>
  );
}
